import React from "react"
import {Box, CircularProgress, Typography} from "@mui/material"

import {useCustomerID} from "../useCustomerID"

import {CreateOfferButton} from "./CreateOfferButton"
import {OfferCard} from "./OfferCard"

import {useCustomerOffers} from "@indebted/api/customerOffersAPI"
import {useNotification} from "@indebted/components/Notification"

type CustomerOffersProps = {
	enableCustomerOffersV2?: boolean
	minInstalmentAmount?: number
	balanceInCents?: number
}

function CustomerOffers({
	enableCustomerOffersV2 = false,
	minInstalmentAmount = 10,
	balanceInCents = 0,
}: CustomerOffersProps) {
	const customerID = useCustomerID()
	const {notification} = useNotification()

	const {data, isLoading, isRefreshing, error} = useCustomerOffers(customerID)

	React.useEffect(() => {
		if (error) {
			notification.error((error as {Message?: string}).Message || "Failed to load offers")
		}
	}, [error, notification])

	const activeOffers = data?.ActiveOffers || []
	const inactiveOffers = data?.InactiveOffers || []

	return (
		<Box p={3}>
			<Box display="flex" justifyContent="flex-end" mb={2}>
				<CreateOfferButton
					enableCustomerOffersV2={enableCustomerOffersV2}
					minInstalmentAmount={minInstalmentAmount}
					balanceInCents={balanceInCents}
				/>
			</Box>

			{isLoading ? (
				<Box display="flex" justifyContent="center" p={4}>
					<CircularProgress />
				</Box>
			) : activeOffers.length === 0 && inactiveOffers.length === 0 ? (
				<Box p={4} textAlign="center">
					<Typography variant="body1" color="textSecondary">
						No offers found for this customer
					</Typography>
				</Box>
			) : (
				<Box display="flex" flexDirection="column" style={{gap: "40px"}}>
					{isRefreshing && (
						<Box display="flex" justifyContent="center">
							<CircularProgress size={24} />
						</Box>
					)}
					{activeOffers.length > 0 && (
						<Box>
							<Box mb={2} pb={0.5} borderBottom="1px solid #e0e0e0">
								<Typography variant="subtitle1" style={{fontWeight: 500, color: "#000000"}}>
									Offers
								</Typography>
							</Box>
							<Box display="flex" flexDirection="column" style={{gap: "16px"}}>
								{activeOffers.map((offer) => (
									<OfferCard key={offer.OfferID} offer={offer} />
								))}
							</Box>
						</Box>
					)}

					{inactiveOffers.length > 0 && (
						<Box>
							<Box mb={2} pb={0.5} borderBottom="1px solid #e0e0e0">
								<Typography variant="subtitle1" style={{fontWeight: 500, color: "#757575"}}>
									Offers History
								</Typography>
							</Box>
							<Box display="flex" flexDirection="column" style={{gap: "16px"}}>
								{inactiveOffers.map((offer) => (
									<OfferCard key={offer.OfferID} offer={offer} />
								))}
							</Box>
						</Box>
					)}
				</Box>
			)}
		</Box>
	)
}

export {CustomerOffers}
